import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { isMilestoneReached } from '../../../utils/milestoneUtils';

const FILL_MS = 480;
const CHECK_MS = 620;
const EXIT_MS = 420;

/**
 * When a milestone drops out of `entries` because it was reached, play a
 * fill → check → pop-out sequence before removing it from the list.
 */
export function useMilestoneCelebrations(entries, { tasks = [], statuses = [], nestedSprints = [] } = {}) {
  const [celebrating, setCelebrating] = useState([]);
  const prevEntriesRef = useRef([]);
  const isInitialRef = useRef(true);
  const scheduledRef = useRef(new Set());

  const scheduleCelebration = useCallback((id, entry, orderIndex) => {
    if (scheduledRef.current.has(id)) return;
    scheduledRef.current.add(id);

    setCelebrating((prev) => [
      ...prev,
      { id, entry, orderIndex, phase: 'fill' },
    ]);

    window.setTimeout(() => {
      setCelebrating((prev) => prev.map((c) => (
        c.id === id ? { ...c, phase: 'check' } : c
      )));
    }, FILL_MS);

    window.setTimeout(() => {
      setCelebrating((prev) => prev.map((c) => (
        c.id === id ? { ...c, phase: 'exit' } : c
      )));
    }, FILL_MS + CHECK_MS);

    window.setTimeout(() => {
      setCelebrating((prev) => prev.filter((c) => c.id !== id));
      scheduledRef.current.delete(id);
    }, FILL_MS + CHECK_MS + EXIT_MS);
  }, []);

  useEffect(() => {
    const prevEntries = prevEntriesRef.current;
    const currentIds = new Set(entries.map((e) => e.milestone.id));

    if (isInitialRef.current) {
      isInitialRef.current = false;
      prevEntriesRef.current = entries;
      return undefined;
    }

    prevEntries.forEach((entry, orderIndex) => {
      const id = entry.milestone.id;
      if (!currentIds.has(id)) {
        const milestone = (tasks || []).find((t) => t.id === id);
        if (milestone && isMilestoneReached(milestone, tasks, nestedSprints, statuses)) {
          scheduleCelebration(id, entry, orderIndex);
        }
      }
    });

    prevEntriesRef.current = entries;
    return undefined;
  }, [entries, tasks, statuses, nestedSprints, scheduleCelebration]);

  const displayItems = useMemo(() => {
    const items = entries.map((entry) => ({
      ...entry,
      celebrationPhase: null,
      isCelebrating: false,
    }));

    if (celebrating.length === 0) return items;

    const sortedCelebrating = [...celebrating].sort((a, b) => a.orderIndex - b.orderIndex);

    sortedCelebrating.forEach((c) => {
      const insertAt = Math.min(c.orderIndex, items.length);
      items.splice(insertAt, 0, {
        ...c.entry,
        reached: c.phase !== 'fill',
        celebrationPhase: c.phase,
        isCelebrating: true,
      });
    });

    return items;
  }, [entries, celebrating]);

  return {
    displayItems,
    isCelebrating: celebrating.length > 0,
  };
}
